import { useCallback, useEffect, useState } from 'react'

type ExpiringStorageValue<T> = {
  value: T
  expires?: number // timestamp when the item expires
}

/**
 * A hook that provides localStorage functionality with expiration (TTL) support
 *
 * @param key The localStorage key to use
 * @param initialValue The initial value to use if no value is found in localStorage
 * @param options Configuration options for expiration
 * @returns [storedValue, setValue, removeValue] tuple
 */
export default function useExpiringLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: {
    expires?: Date // Specific expiration date
    maxAge?: number // Max age in seconds
  }
) {
  // Helper function to get item from localStorage with expiration check
  const readFromStorage = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)

      if (!item) {
        return initialValue
      }

      const parsedItem: ExpiringStorageValue<T> = JSON.parse(item)

      // Check if the item has expired
      if (parsedItem.expires && Date.now() > parsedItem.expires) {
        window.localStorage.removeItem(key)
        return initialValue
      }

      return parsedItem.value
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }, [key, initialValue])

  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => readFromStorage())

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback(
    (value: T | ((val: T) => T), newOptions?: typeof options) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value

        // Calculate expiration time if provided
        let expires: number | undefined = undefined

        const mergedOptions = { ...options, ...newOptions }

        if (mergedOptions.expires) {
          expires = mergedOptions.expires.getTime()
        } else if (mergedOptions.maxAge) {
          expires = Date.now() + mergedOptions.maxAge * 1000
        }

        // Save to localStorage
        const storageValue: ExpiringStorageValue<T> = {
          value: valueToStore,
          expires,
        }

        window.localStorage.setItem(key, JSON.stringify(storageValue))

        // Save state
        setStoredValue(valueToStore)
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, options, storedValue]
  )

  // Remove the item from localStorage
  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key)
      setStoredValue(initialValue)
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  }, [key, initialValue])

  // Check for expired items on mount and when key/dependencies change
  useEffect(() => {
    setStoredValue(readFromStorage())
  }, [key, readFromStorage])

  return [storedValue, setValue, removeValue] as const
}
